import { getAppBridge } from './utils.js';

let appBridge = null;
let instanceIds = [];

const logEl = document.getElementById('eventLog');
const log = (msg, type = 'info') => {
  const ts = new Date().toLocaleTimeString();
  const color =
    type === 'error'
      ? 'text-red-600'
      : type === 'success'
      ? 'text-green-600'
      : type === 'warn'
      ? 'text-yellow-600'
      : 'text-gray-700';
  logEl.innerHTML += `<div class="${color}">[${ts}] ${msg}</div>`;
  logEl.scrollTop = logEl.scrollHeight;
};

document.getElementById('btnClearLog').addEventListener('click', () => {
  logEl.innerHTML = '';
});

const displayCallContext = (ctx) => {
  document.getElementById('callContextGuest').textContent = ctx?.guest
    ? JSON.stringify(ctx.guest, null, 2)
    : '(none — internal call)';
  document.getElementById('callContextChain').textContent = ctx?.callChain
    ? JSON.stringify(ctx.callChain, null, 2)
    : '(empty — direct call)';
};

const refreshAppList = () => {
  const apps = appBridge?.getApps() || [];
  document.getElementById('appList').textContent = apps.length
    ? apps.map((a) => `${a.uuid}`).join('\n')
    : '(no apps)';
};

class Loan extends ice.ScriptingObject {
  constructor() {
    super('Loan');
    this.loanId = 'E2E-001';
    this.onSaved = new ice.Event({
      name: 'onSaved',
      objectId: 'Loan',
    });
    this.onPreSave = new ice.Event({
      name: 'onPreSave',
      objectId: 'Loan',
    });
    this.onLoanAmountChanged = new ice.Event({
      name: 'onLoanAmountChanged',
      objectId: 'Loan',
    });
    this.onLoanTermChanged = new ice.Event({
      name: 'onLoanTermChanged',
      objectId: 'Loan',
    });
    this.onDownPaymentChanged = new ice.Event({
      name: 'onDownPaymentChanged',
      objectId: 'Loan',
    });
  }

  getLoanDetails = () => {
    const ctx = this.getLoanDetails.callContext;
    log('Loan.getLoanDetails() invoked');
    displayCallContext(ctx);
    return {
      id: this.loanId,
      firstName: 'Jane',
      lastName: 'Doe',
      ssn: '123456789',
      amount: 500000,
      term: 30,
      downPayment: 50000,
      creditScore: 750,
    };
  };

  setCreditScore = (creditScore) => {
    const ctx = this.setCreditScore.callContext;
    log(`Loan.setCreditScore(${creditScore}) invoked`);
    displayCallContext(ctx);
    return { updated: true, creditScore };
  };
}

class Inventory extends ice.ScriptingObject {
  constructor() {
    super('Inventory');
  }
  getItems = () => {
    const ctx = this.getItems.callContext;
    log('Inventory.getItems() invoked');
    displayCallContext(ctx);
    return [
      { id: 1, name: 'Widget A' },
      { id: 2, name: 'Widget B' },
    ];
  };
}

let loanObj = null;
let inventoryAdded = false;

const init = async () => {
  log('Initializing E2E Host...');

  try {
    appBridge = await getAppBridge();
  } catch (e) {
    log(`ERROR: Failed to create AppBridge — ${e.message}`, 'error');
    return;
  }

  loanObj = new Loan();
  appBridge.addScriptingObject(loanObj);
  log('Registered: Loan scripting object');

  // --- App Loading ---

  document.getElementById('btnOpenApp').addEventListener('click', async () => {
    try {
      const instanceId = await appBridge.openApp({
        id: 'pricingservice',
        frameOptions: {
          containerId: 'appContainer',
          title: 'Pricing Service',
        },
      });
      instanceIds.push(instanceId);
      log(`openApp("pricingservice") → instanceId=${instanceId}`, 'success');
    } catch (e) {
      log(`openApp("pricingservice") failed: ${e.message}`, 'error');
    }
    refreshAppList();
  });

  document
    .getElementById('btnOpenCredit')
    .addEventListener('click', async () => {
      try {
        const instanceId = await appBridge.openApp({
          id: 'creditservice',
          frameOptions: {
            containerId: 'appContainer',
            title: 'Credit Service',
          },
        });
        instanceIds.push(instanceId);
        log(`openApp("creditservice") → instanceId=${instanceId}`, 'success');
      } catch (e) {
        log(`openApp("creditservice") failed: ${e.message}`, 'error');
      }
      refreshAppList();
    });

  document
    .getElementById('btnOpenValidation')
    .addEventListener('click', async () => {
      try {
        const instanceId = await appBridge.openApp({
          id: 'loanvalidation',
          frameOptions: {
            containerId: 'appContainer',
            title: 'Loan Validation',
          },
        });
        instanceIds.push(instanceId);
        log(`openApp("loanvalidation") → instanceId=${instanceId}`, 'success');
      } catch (e) {
        log(`openApp("loanvalidation") failed: ${e.message}`, 'error');
      }
      refreshAppList();
    });

  document
    .getElementById('btnOpenE2EGuest')
    .addEventListener('click', async () => {
      try {
        const instanceId = await appBridge.openApp({
          id: 'e2etestguest',
          frameOptions: {
            containerId: 'appContainer',
            title: 'E2E Test Guest',
          },
        });
        instanceIds.push(instanceId);
        log(`openApp("e2etestguest") → instanceId=${instanceId}`, 'success');
      } catch (e) {
        log(`openApp("e2etestguest") failed: ${e.message}`, 'error');
      }
      refreshAppList();
    });

  document
    .getElementById('btnOpenNestedChain')
    .addEventListener('click', async () => {
      try {
        const instanceId = await appBridge.openApp({
          id: 'e2eintermediateguest',
          frameOptions: {
            containerId: 'appContainer',
            title: 'Intermediate Guest B',
          },
          metadata: { vendor: 'Acme Credit Corp', tier: 'premium' },
        });
        instanceIds.push(instanceId);
        log(
          `openApp("e2eintermediateguest" + metadata) → instanceId=${instanceId}`,
          'success',
        );
        log(
          "Chain: Host A → Intermediate B → Grandchild C. Use C's buttons to test callChain.",
        );
      } catch (e) {
        log(`openApp("e2eintermediateguest") failed: ${e.message}`, 'error');
      }
      refreshAppList();
    });

  document
    .getElementById('btnOpenWithMetadata')
    .addEventListener('click', async () => {
      try {
        const instanceId = await appBridge.openApp({
          id: 'pricingservice',
          frameOptions: {
            containerId: 'appContainer',
            title: 'Pricing (with metadata)',
          },
          metadata: { vendor: 'AcmeCorp', tier: 'premium', region: 'US-East' },
        });
        instanceIds.push(instanceId);
        log(
          `openApp("pricingservice" + metadata) → instanceId=${instanceId}`,
          'success',
        );
      } catch (e) {
        log(`openApp with metadata failed: ${e.message}`, 'error');
      }
      refreshAppList();
    });

  // --- Scripting Object Management ---

  document.getElementById('btnAddSO').addEventListener('click', () => {
    if (inventoryAdded) {
      log('Inventory already added', 'warn');
      return;
    }
    appBridge.addScriptingObject(new Inventory());
    inventoryAdded = true;
    log('Added "Inventory" scripting object', 'success');
  });

  document.getElementById('btnRemoveSO').addEventListener('click', () => {
    try {
      appBridge.removeScriptingObject('Inventory');
      inventoryAdded = false;
      log('Removed "Inventory" scripting object', 'success');
    } catch (e) {
      log(`removeScriptingObject failed: ${e.message}`, 'error');
    }
  });

  document.getElementById('btnRemoveAllSO').addEventListener('click', () => {
    appBridge.removeAllScriptingObjects();
    inventoryAdded = false;
    log('Removed all scripting objects', 'success');
  });

  // --- Event Dispatching ---

  document
    .getElementById('btnDispatchPreSave')
    .addEventListener('click', async () => {
      log('Dispatching onPreSave to all apps...');
      try {
        const results = await appBridge.dispatchEvent({
          event: loanObj.onPreSave,
          eventParams: {
            id: loanObj.loanId,
            firstName: 'Jane',
            lastName: 'Doe',
            amount: 500000,
            creditScore: 750,
          },
          eventOptions: { timeout: 5000 },
        });
        const resultDisplay = Array.isArray(results)
          ? JSON.stringify(results, null, 2)
          : '(no results)';
        document.getElementById(
          'dispatchResult',
        ).textContent = `onPreSave results:\n${resultDisplay}`;
        log(
          `onPreSave dispatched, ${
            Array.isArray(results) ? results.length : 0
          } responses`,
          'success',
        );
      } catch (e) {
        log(`Dispatch failed: ${e.message}`, 'error');
      }
    });

  document
    .getElementById('btnDispatchAmount')
    .addEventListener('click', async () => {
      log('Dispatching onLoanAmountChanged...');
      try {
        const results = await appBridge.dispatchEvent({
          event: loanObj.onLoanAmountChanged,
          eventParams: { amount: 750000 },
        });
        document.getElementById(
          'dispatchResult',
        ).textContent = `onLoanAmountChanged results:\n${JSON.stringify(
          results,
          null,
          2,
        )}`;
        log('onLoanAmountChanged dispatched', 'success');
      } catch (e) {
        log(`Dispatch failed: ${e.message}`, 'error');
      }
    });

  document
    .getElementById('btnDispatchTerm')
    .addEventListener('click', async () => {
      log('Dispatching onLoanTermChanged...');
      try {
        const results = await appBridge.dispatchEvent({
          event: loanObj.onLoanTermChanged,
          eventParams: { term: 15 },
        });
        document.getElementById(
          'dispatchResult',
        ).textContent = `onLoanTermChanged results:\n${JSON.stringify(
          results,
          null,
          2,
        )}`;
        log('onLoanTermChanged dispatched', 'success');
      } catch (e) {
        log(`Dispatch failed: ${e.message}`, 'error');
      }
    });

  // --- App Lifecycle ---

  document.getElementById('btnCloseApp').addEventListener('click', async () => {
    if (!instanceIds.length) {
      log('No apps to close', 'warn');
      return;
    }
    const instanceId = instanceIds.shift();
    log(`Closing app instanceId=${instanceId}...`);
    try {
      await appBridge.closeApp(instanceId);
      log(`closeApp("${instanceId}") succeeded`, 'success');
    } catch (e) {
      log(`closeApp failed: ${e.message}`, 'error');
    }
    refreshAppList();
  });

  document.getElementById('btnCloseAll').addEventListener('click', async () => {
    log('Closing all apps...');
    try {
      await appBridge.closeAllApps();
      instanceIds = [];
      log('closeAllApps() succeeded', 'success');
    } catch (e) {
      log(`closeAllApps failed: ${e.message}`, 'error');
    }
    refreshAppList();
  });

  document.getElementById('btnListApps').addEventListener('click', () => {
    refreshAppList();
    const apps = appBridge.getApps();
    log(`Listed ${apps.length} active app(s)`);
  });

  setInterval(refreshAppList, 3000);

  log('E2E Host ready', 'success');
};

window.addEventListener('DOMContentLoaded', init);
